<template>
  <VisionLink
    :class="
      bem({
        'has-icon': !!props.block.icon?.icon,
        'has-theme': !!props.block.theme,
        'colour-': props.block.colour,
        isActive,
      })
    "
    :to="buildInternalLink(props.block.link)"
    :style="
      !!props.block.theme &&
      `--theme-bg: ${props.block.theme.buttonBackgroundColour.hex};
      --theme-text: ${props.block.theme.buttonTextColour.hex}`
    "
    @mouseenter="handleHover(true)"
    @mouseleave="handleHover(false)"
    @click="handleClick"
  >
    <span :class="bem('text')">
      <span
        :class="bem('text-inner')"
        :data-text="props.block.text"
      >
        {{ props.block.text }}
      </span>
    </span>
    <div
      v-if="props.block.icon?.icon"
      :class="bem('icon-container')"
    >
      <VisionIconButton
        :icon="props.block.icon.icon"
        size="x-small"
        icon-colour="black"
        background-colour="white"
        :parent-hover="isHovered"
        :class="bem('icon-button')"
      />
    </div>
  </VisionLink>
</template>
<script setup lang="ts">
import { throttleFunction } from '~/assets/js/helpers'

interface StickyCtaButtonProps {
  block: {
    text: string
    link: any
    trigger: number
    theme: any
    colour: string
    icon: {
      icon: string
    }
    trackingId?: string
  }
}

const props = defineProps<StickyCtaButtonProps>()
const bem = useBEM('StickyCtaButton')

const { $track } = useNuxtApp() as any
const { buildInternalLink } = useUrlBuilder()

const isHovered = ref(false)
const isActive = ref(props.block.trigger === 0)

function handleHover(hover: boolean) {
  isHovered.value = hover
}
  
function handleScroll() {
  const triggerVh = props.block.trigger
  const viewportHeight = window.innerHeight
  const triggerPx = (triggerVh / 100) * viewportHeight

  isActive.value = window.scrollY > triggerPx
}

async function handleClick() {
  await $track.global.linkClicked({
    interactionElement: props.block.trackingId || '',
    interactionCategory: 'Sticky CTA Button',
    linkText: props.block.text,
  }, 'Clicked Sticky CTA Button')
}


const throttledScrollHandler = throttleFunction(() => {
  handleScroll()
}, 500)

onMounted(() => {
  if (props.block.trigger > 0) {
    window.addEventListener('scroll', throttledScrollHandler)
    handleScroll()
  }
})

onUnmounted(() => {
  if (props.block.trigger > 0) {
    window.removeEventListener('scroll', throttledScrollHandler)
  }
})
</script>
<style lang="postcss">
.StickyCtaButton {
  @apply fixed top-1 md:top-2 right-1/2 z-[8]
  py-2.5 px-5 backdrop-blur-dark text-white
  rounded-lg min-h-[auto] md:min-h-14
  flex items-center justify-between gap-4
  duration-500 ease-in-out
  pointer-events-none -translate-y-3
  translate-x-1/2;
  clip-path: inset(0 25% 100% 25% round 8px);
  transition-property: transform, clip-path, background;

  @screen md {
    @apply top-4 right-4 translate-x-0;
    clip-path: inset(0 0 100% 50% round 8px);
  }
}

.StickyCtaButton--has-icon {
  @apply pr-2.5;
}

.StickyCtaButton__text {
  @apply block font-bold text-base translate-y-px
  overflow-clip;
}

.StickyCtaButton__text-inner {
  @apply relative inline-block
  -translate-y-full transition-transform duration-300
  delay-150 text-nowrap;

  &:after {
    content: attr(data-text);
    @apply absolute inset-0 -translate-y-full;
  }
}

.StickyCtaButton__icon-container {
  @apply overflow-clip;
}

.StickyCtaButton__icon-button {
  @apply -translate-y-full transition-transform duration-300;
}

.StickyCtaButton--is-active {
  @apply pointer-events-auto -translate-y-0;
  clip-path: inset(0 0 0 0 round 8px);

  .StickyCtaButton__text-inner {
    @apply -translate-y-0;
  }

  .StickyCtaButton__icon-button {
    @apply -translate-y-0 delay-150 p-2;
  }
}

.StickyCtaButton:hover {
  @apply bg-opacity-80 text-white;

  .StickyCtaButton__text-inner {
    @apply translate-y-full delay-0;
  }
}

/* Light Version */
.StickyCtaButton--colour-light {
  @apply text-black hover:text-black backdrop-blur-light;
}

/* Theme */
.StickyCtaButton--has-theme {
  @apply bg-[var(--theme-bg)] text-[var(--theme-text)] hover:text-[var(--theme-text)];

  .StickyCtaButton__icon-button {
    --vision-icon-button-bg-colour: theme('colors.black');
    --vision-icon-button-icon-colour: var(--theme-bg);
  }

  &:not(.StickyCtaButton--has-icon) {
    .StickyCtaButton__text-inner::after {
      @apply text-[var(--theme-bg)];
    }
  }
}
</style>
